import { useMemo } from 'react'
import { CanonicalLoad } from '@numeo/types-core'
import type { BoxTruckRefinements } from '../components/BoxTruckRefinementBar'
import type { BoxTruckCompanyFilterOptions } from '../components/BoxTruckCompanyFilter'
import { EXCLUDED_SOURCE_GROUPS, brokerEmail, brokerPhone, numeoId, sourceGroupKey, vehicleLabel } from '../utils/loadFields'
import { type BrokerListMatcher, MATCHES_NO_BROKER } from '../utils/brokerListMatcher'
import { type BoxTruckSourceGroup } from '../components/BoxTruckSourceBar'
import {
    type BoxTruckCapacityFilters,
    BoxTruckLoadType,
    DAT_SOURCE,
    matchesCapacity,
    type RequestedSupplements,
    SOURCE,
    SUPPLEMENT_SOURCES,
} from './requestMapping'
import type { BoxTruckCapacityLimits } from '@numeo/types-core'

/** Chip order for the Sources bar — the box-truck-relevant sources first, everything else after (alpha). */
const SOURCE_GROUP_ORDER = ['SYLECTUS', 'DAT', 'TRUCKSTOP', 'ARRIVE', 'ECHO']

/** The chip of the feed every search asks for (see SUPPLEMENT_SOURCES for why it is never optional). */
const SYLECTUS_SOURCE_GROUP = 'SYLECTUS'

/** Sort rank for a source-group key — its index in SOURCE_GROUP_ORDER, unranked keys last. */
const rankSourceGroup = (key: string) => (SOURCE_GROUP_ORDER.indexOf(key) === -1 ? SOURCE_GROUP_ORDER.length : SOURCE_GROUP_ORDER.indexOf(key))

/** Apply these controls only to the two feeds they own. Sylectus needs client filtering because it never states
 *  trailer length/load type; DAT needs the exact customized per-group refinement after its subtype-filtered
 *  page arrives. Every other source keeps its provider-defined equipment and capacity semantics. */
const passesBoxTruckSourceCapacity = (load: CanonicalLoad, caps: BoxTruckCapacityFilters): boolean =>
    (load.sourceType !== SOURCE && load.sourceType !== DAT_SOURCE) || matchesCapacity(load, caps)

interface BoxTruckBoardViewParams {
    boardLoads: CanonicalLoad[]
    equipmentLabels: string[]
    /** The operator's capacity filters for the Box Truck Sylectus and DAT feeds. */
    fullPartial?: BoxTruckLoadType
    lengthFt?: number | null
    weightLbs?: number | null
    capacityLimits?: BoxTruckCapacityLimits
    includeUnknownCapacity?: boolean
    hiddenSources: Set<string>
    query: string
    /** Client-side refinements (load-hub Row-2 semantics): a set bound EXCLUDES loads missing that field —
     *  an unknown distance/rate/weight can't prove it meets the bound. Age is NOT among them: it is the
     *  request's own maxPostedAgeHours, applied by the server over the whole matching set, because a client
     *  cap can only ever trim the one page it was handed. */
    refinements?: BoxTruckRefinements
    /** Blocked brokers (my_brokers type IN_BLACKLIST), matched by MC, email or (flagged) Sylectus brokerage. */
    isBlockedBroker?: BrokerListMatcher
    /** Favorite brokers (my_brokers type FAVORITE) — the "Show Only Preferred" view's identity, matched like the blocked list. */
    isPreferredBroker?: BrokerListMatcher
    /** Load-hub's Company filter (Show Only / Exclude by name + blocked-list view), box-truck edition. */
    companyFilter?: BoxTruckCompanyFilterOptions
    /** The supplements the board ASKS for (DAT, BeOn), the same object its request gate is built from. Each one
     *  asked for keeps a chip even at 0 rows, as Sylectus always does — on a tab that has not searched yet, or
     *  a search that found nothing — because a chip that vanished there read as the source not working.
     *  Omitted (a pinned board): Sylectus alone. */
    requestedSupplements?: RequestedSupplements
}

interface BoxTruckBoardView {
    equipmentFiltered: CanonicalLoad[]
    sourceGroups: BoxTruckSourceGroup[]
    visibleLoads: CanonicalLoad[]
}

/**
 * Derived board view for the Box Truck loadboard: client equipment refinement, the Sources-bar groups, and
 * the text/hidden-source filtered visible rows. Split out of the page component so the page stays focused on
 * state/handlers/JSX.
 */
export const useBoxTruckBoardView = ({
    boardLoads,
    equipmentLabels,
    fullPartial,
    lengthFt,
    weightLbs,
    capacityLimits,
    includeUnknownCapacity,
    hiddenSources,
    query,
    refinements,
    isBlockedBroker = MATCHES_NO_BROKER,
    isPreferredBroker = MATCHES_NO_BROKER,
    companyFilter,
    requestedSupplements,
}: BoxTruckBoardViewParams): BoxTruckBoardView => {
    // Client equipment refinement over the server results. The server filters the Sylectus feed to the exact
    // vehicle sizes, but the live-DAT half can only be narrowed to the coarse unified class (SV = BOTH
    // Cargo Van AND Sprinter), so a "Sprinter" search still returns DAT cargo vans. Refine here by the exact
    // box-truck label so the board shows only the picked classes — and drop Telegram entirely.
    const equipmentFiltered = useMemo(() => {
        const wanted = equipmentLabels.length ? new Set(equipmentLabels) : null
        return boardLoads.filter((load) => {
            if (EXCLUDED_SOURCE_GROUPS.has(sourceGroupKey(load))) return false // Telegram etc. — never on the board
            // Company filter (load-hub applyCompanyFilter semantics) — applied HERE so the loaded-count chips
            // don't count hidden rows.
            const isBlocked = isBlockedBroker(load)
            const listType = companyFilter?.listType ?? 'all'
            const exclude = companyFilter?.mode === 'exclude'
            if (listType === 'blocked') {
                // Blocked-list view (name filter ignored, like load-hub's applyCompanyFilter): Show Only
                // keeps ONLY blocked brokers' loads, Exclude hides them (and shows everything else).
                if (exclude ? isBlocked : !isBlocked) return false
            } else if (listType === 'preferred') {
                // Preferred view, same mode semantics: Show Only keeps favorites, Exclude hides them.
                const isPreferred = isPreferredBroker(load)
                if (exclude ? isPreferred : !isPreferred) return false
            } else {
                if (isBlocked) return false // "Show All Non-blocked" — the default board behaviour
                if (companyFilter && companyFilter.companyNames.length > 0) {
                    const nameMatches = companyFilter.companyNames.includes(load.brokerCompanyName || '')
                    if (companyFilter.mode === 'show' && !nameMatches) return false
                    if (companyFilter.mode === 'exclude' && nameMatches) return false
                }
            }
            if (!passesBoxTruckSourceCapacity(load, { fullPartial, lengthFt, weightLbs, capacityLimits, includeUnknownCapacity })) return false
            if (!wanted) return true
            // Only the Sylectus feed carries a fine vehicle size to refine on (Cargo Van vs Sprinter, small
            // vs large straight); DAT/supplement loads have no sylectusVehicleSize, so `vehicleLabel` returns a
            // DIFFERENT vocabulary (Straight Truck / Cargo Van) that would never match the box-truck labels —
            // dropping every DAT row for a "Large Straight"/"Sprinter" pick. So keep them.
            //
            // Do NOT "fix" this by filtering DAT rows on their equipment code. The server already asked DAT
            // for exactly the codes the picked options map to, so every DAT row on the board is one the
            // selection requested; re-filtering here by a label drawn from a different vocabulary can only
            // drop rows the search deliberately fetched. Size is separated by CAPACITY (Length/Weight),
            // applied above by passesBoxTruckSourceCapacity, not by re-reading the class.
            if (!load.details?.sylectusVehicleSize) return true
            return wanted.has(vehicleLabel(load))
        })
    }, [
        boardLoads,
        equipmentLabels,
        fullPartial,
        lengthFt,
        weightLbs,
        capacityLimits,
        includeUnknownCapacity,
        isBlockedBroker,
        isPreferredBroker,
        companyFilter,
    ])

    /**
     * Every client-side refinement applied — the Row-2 bounds, the age cap and the text filter — but NOT the
     * Sources-bar hide. This is the board as the operator refined it, which both the chip counts and the
     * table read from, so the number on a chip is the number of rows that source contributes below it.
     */
    const refinedLoads = useMemo(() => {
        const q = query.trim().toLowerCase()
        // Parsed once per change; NaN (empty/garbage input) = that filter off — load-hub's parseFloat guards.
        const minMi = refinements?.minMi ? parseFloat(refinements.minMi) : NaN
        const maxMi = refinements?.maxMi ? parseFloat(refinements.maxMi) : NaN
        const minR = refinements?.minR ? parseFloat(refinements.minR) : NaN
        const minRpm = refinements?.minT ? parseFloat(refinements.minT) : NaN
        const minW = refinements?.minWeight ? parseFloat(refinements.minWeight) : NaN
        const maxW = refinements?.maxWeight ? parseFloat(refinements.maxWeight) : NaN
        return equipmentFiltered.filter((load) => {
            // `!= null`, not truthiness — a 0-mile/0-rate value is a VALUE that can satisfy a bound
            // (0 >= 0), not a missing field. Matches the weight checks below.
            if (!isNaN(minMi) && !(load.distanceMiles != null && load.distanceMiles >= minMi)) return false
            if (!isNaN(maxMi) && !(load.distanceMiles != null && load.distanceMiles <= maxMi)) return false
            if (!isNaN(minR) && !(load.rateAmountUsd != null && load.rateAmountUsd >= minR)) return false
            if (!isNaN(minRpm) && minRpm > 0) {
                if (!load.rateAmountUsd || !load.distanceMiles) return false
                if (load.rateAmountUsd / load.distanceMiles < minRpm) return false
            }
            if (!isNaN(minW) && !(load.weightLbs != null && load.weightLbs >= minW)) return false
            if (!isNaN(maxW) && !(load.weightLbs != null && load.weightLbs <= maxW)) return false
            // Contact requirement — same broker→poster fallback the Contact column renders, so what the
            // filter keeps is exactly what shows an email/phone in the table.
            if (refinements?.contact === 'email' && !brokerEmail(load)) return false
            if (refinements?.contact === 'phone' && !brokerPhone(load)) return false
            if (!q) return true
            const haystack = [
                load.postersReferenceId,
                load.details?.sylectusRefNo,
                numeoId(load),
                load.pickupCity,
                load.pickupStateProv,
                load.deliveryCity,
                load.deliveryStateProv,
                load.brokerCompanyName,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
            return haystack.includes(q)
        })
    }, [equipmentFiltered, query, refinements])

    /**
     * The chips the bar renders: the sources whose rows the operator can actually see, counted the same way.
     *
     * Counted over the REFINED rows, not the whole fetched board. A count taken before the refinements read
     * as a promise the table does not keep: a search tab opens on a one-hour age cap while the query itself
     * reaches back twelve, so the chip said 1 646 above a table showing twelve — the same twelve the age cap
     * had left, with no way to tell from the screen where the other 1 634 went.
     *
     * The one filter it stays blind to is the chip's OWN hide: switching a source off leaves its rows
     * counted, so the way back is always the chip itself and the bar can never become a one-way door.
     *
     * A source the board ASKS for always has a chip, at 0 when it has no rows: an empty bar on a fresh tab,
     * or a missing chip after a search, read as that source not working. A source it never asked for has no
     * rows and so no chip, which is right: DAT off is switched from Advanced Filters, and a second, emptier
     * control for it here would be two answers to one question.
     */
    const sourceGroups = useMemo<BoxTruckSourceGroup[]>(() => {
        // A supplement's request name is also its chip key (DAT → DAT, BEON → BEON), so it seeds the map as is.
        const counts = new Map<string, number>([[SYLECTUS_SOURCE_GROUP, 0]])
        for (const source of SUPPLEMENT_SOURCES) if (requestedSupplements?.[source]) counts.set(source, 0)
        // Every source that HAS rows on the board starts at zero, so a refinement can take a source's count
        // to nothing without taking its chip: the chip at 0 is what says the rows were fetched and filtered
        // out, which is the question this whole bar answers. It also keeps a switched-off source clickable,
        // and keeps the bar itself mounted while a text filter matches nothing.
        for (const load of equipmentFiltered) {
            const key = sourceGroupKey(load)
            if (key) counts.set(key, 0)
        }
        for (const load of refinedLoads) {
            const key = sourceGroupKey(load)
            if (!key) continue
            counts.set(key, (counts.get(key) ?? 0) + 1)
        }
        return [...counts.entries()]
            .map(([key, count]) => ({ key, count }))
            .sort((a, b) => rankSourceGroup(a.key) - rankSourceGroup(b.key) || a.key.localeCompare(b.key))
    }, [equipmentFiltered, refinedLoads, requestedSupplements])

    const visibleLoads = useMemo(() => refinedLoads.filter((load) => !hiddenSources.has(sourceGroupKey(load))), [refinedLoads, hiddenSources])

    return { equipmentFiltered, sourceGroups, visibleLoads }
}
